import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import NoderaMark from "@/components/NoderaMark";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setLoading(true);
      await api.post("/subscribe", { email });
      toast.success("Subscribed. Expect our first update soon.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-slate-50 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-5">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="inline-flex w-9 h-9 items-center justify-center text-[#1F4853]">
                <NoderaMark size={32} />
              </span>
              <span className="font-display font-bold text-slate-900 text-xl">
                Nodera <span className="text-slate-400 font-medium">Health</span>
              </span>
            </Link>
            <p className="text-sm text-slate-600 leading-relaxed max-w-xs">
              The infrastructure layer enabling licensed clinics to deploy and scale cash-pay programs through workflow automation, vendor coordination, and financial workflow enablement.
            </p>
            <form onSubmit={onSubscribe} data-testid="footer-subscribe-form" className="flex items-center gap-2 max-w-sm">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Work email"
                data-testid="footer-subscribe-email"
                className="flex-1 bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-subscribe-submit"
                className="bg-slate-900 text-white rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {loading ? "…" : "Join"}
              </button>
            </form>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-4">Platform</h4>
            <ul className="space-y-2.5 text-sm text-slate-600">
              <li><Link to="/platform" className="hover:text-slate-900">Overview</Link></li>
              <li><Link to="/programs" className="hover:text-slate-900">Programs</Link></li>
              <li><Link to="/vendors" className="hover:text-slate-900">For vendors</Link></li>
              <li><Link to="/pricing" className="hover:text-slate-900">Pricing</Link></li>
              <li><Link to="/demo" className="hover:text-slate-900">Book a demo</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-4">Company</h4>
            <ul className="space-y-2.5 text-sm text-slate-600">
              <li><Link to="/about" className="hover:text-slate-900">About</Link></li>
              <li><Link to="/blog" className="hover:text-slate-900">Blog</Link></li>
              <li><Link to="/contact" className="hover:text-slate-900">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-4">Access</h4>
            <ul className="space-y-2.5 text-sm text-slate-600">
              <li><Link to="/login" className="hover:text-slate-900">Clinic portal login</Link></li>
              <li><Link to="/request-access" className="hover:text-slate-900">Request access</Link></li>
              <li><Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-slate-900">Privacy</Link></li>
              <li><Link to="/terms" data-testid="footer-terms-link" className="hover:text-slate-900">Terms</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-slate-500 max-w-2xl leading-relaxed">
            Nodera Health provides operational infrastructure only and does not provide healthcare services, clinical oversight, prescribing, diagnostics interpretation, or patient care management. Clinics retain full control of clinical decisions. Vendors operate independently. Payment flows are handled by third-party providers.
          </p>
          <p className="text-xs text-slate-500 whitespace-nowrap">© {new Date().getFullYear()} Nodera Health, Inc.</p>
        </div>
      </div>
    </footer>
  );
}
